import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext, API } from '@/App';
import AdminLayout from '@/components/admin/AdminLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  ArrowLeft, Building, User, Calendar, DollarSign, Star, AlertTriangle,
  Ban, CheckCircle, Settings, Mail, Phone, MapPin
} from 'lucide-react';

const STATUS_COLORS = {
  completed: 'bg-green-100 text-green-700',
  pending: 'bg-yellow-100 text-yellow-700',
  cancelled: 'bg-red-100 text-red-700',
  COMPLETED: 'bg-green-100 text-green-700',
  PENDING: 'bg-yellow-100 text-yellow-700',
  CANCELLED: 'bg-red-100 text-red-700'
};

const STATUS_LABELS = {
  completed: 'Concluído',
  pending: 'Pendente',
  cancelled: 'Cancelado',
  COMPLETED: 'Concluído',
  PENDING: 'Pendente',
  CANCELLED: 'Cancelado',
  PAID: 'Pago'
};

export default function AdminProviderDetail() {
  const navigate = useNavigate();
  const { providerId } = useParams();
  const { user } = useContext(AuthContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || user.user_type !== 'admin') {
      navigate('/control-portal-clube/login');
      return;
    }
    fetchProviderDetail();
  }, [user, navigate, providerId]);

  const fetchProviderDetail = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/dashboard-v2/providers/${providerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (error) {
      console.error(error);
      toast.error('Erro ao carregar dados do prestador');
      navigate('/control-portal-clube/providers');
    } finally {
      setLoading(false);
    }
  };

  const handleBlock = async (blocked) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/block-user`, {
        user_id: providerId,
        blocked: blocked
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(blocked ? 'Prestador bloqueado' : 'Prestador desbloqueado');
      fetchProviderDetail();
    } catch (error) {
      toast.error('Erro ao alterar status');
    }
  };

  const getInitials = (name) => {
    if (!name) return '??';
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('pt-BR');
  };

  if (loading) {
    return (
      <AdminLayout title="Carregando..." subtitle="Detalhes do prestador">
        <div className="space-y-6">
          <Skeleton className="h-48 w-full" />
          <div className="grid grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24" />)}
          </div>
        </div>
      </AdminLayout>
    );
  }

  const provider = data?.provider || {};
  const providerScore = data?.provider_score || {};
  const appointments = data?.appointments || [];
  const transfers = data?.transfers || [];
  const reviews = data?.reviews || [];
  const fraudLogs = data?.fraud_logs || [];
  const stats = data?.stats || {};
  const address = provider.address || {};
  const fullAddress = address.full_address ||
    [address.street, address.number, address.neighborhood, address.city, address.state]
      .filter(Boolean)
      .join(', ');

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/control-portal-clube/providers')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Detalhes do Prestador</h1>
            <p className="text-slate-500">Informações completas e histórico</p>
          </div>
        </div>

        {/* Profile Card */}
        <Card className="p-6">
          <div className="flex flex-col md:flex-row gap-6">
            <div className="flex items-start gap-4">
              <Avatar className="w-20 h-20">
                <AvatarFallback className="bg-green-100 text-green-700 text-2xl">
                  {getInitials(provider.business_name || provider.name)}
                </AvatarFallback>
              </Avatar>
              <div>
                <h2 className="text-xl font-bold">{provider.business_name || provider.name}</h2>
                <p className="text-slate-500">{provider.email}</p>
                <div className="flex items-center gap-2 mt-2">
                  {provider.blocked ? (
                    <Badge className="bg-red-100 text-red-700">Bloqueado</Badge>
                  ) : (
                    <Badge className="bg-green-100 text-green-700">Ativo</Badge>
                  )}
                  {provider.doc_type === 'cnpj' ? (
                    <Badge variant="outline"><Building className="w-3 h-3 mr-1" /> Pessoa Jurídica</Badge>
                  ) : (
                    <Badge variant="outline"><User className="w-3 h-3 mr-1" /> Pessoa Física</Badge>
                  )}
                </div>
              </div>
            </div>

            <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-3 bg-blue-50 rounded-lg">
                <p className="text-2xl font-bold text-blue-700">{stats.total_appointments || 0}</p>
                <p className="text-xs text-blue-600">Agendamentos</p>
              </div>
              <div className="text-center p-3 bg-green-50 rounded-lg">
                <p className="text-2xl font-bold text-green-700">{stats.completed_appointments || 0}</p>
                <p className="text-xs text-green-600">Concluídos</p>
              </div>
              <div className="text-center p-3 bg-amber-50 rounded-lg">
                <div className="flex items-center justify-center gap-1">
                  <p className="text-2xl font-bold text-amber-700">{stats.average_rating || 0}</p>
                  <Star className="w-5 h-5 text-amber-500 fill-amber-500" />
                </div>
                <p className="text-xs text-amber-600">{stats.total_reviews || 0} avaliações</p>
              </div>
              <div className="text-center p-3 bg-emerald-50 rounded-lg">
                <p className="text-2xl font-bold text-emerald-700">{formatCurrency(stats.total_earnings)}</p>
                <p className="text-xs text-emerald-600">Ganhos totais</p>
              </div>
            </div>

            <div className="flex gap-2">
              {provider.blocked ? (
                <Button onClick={() => handleBlock(false)} className="bg-green-600 hover:bg-green-700">
                  <CheckCircle className="w-4 h-4 mr-2" /> Desbloquear
                </Button>
              ) : (
                <Button variant="destructive" onClick={() => handleBlock(true)}>
                  <Ban className="w-4 h-4 mr-2" /> Bloquear
                </Button>
              )}
            </div>
          </div>

          {/* Info Adicional */}
          <div className="mt-6 pt-6 border-t grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-slate-500">Documento</p>
              <p className="text-sm font-medium">{provider.cpf_cnpj || 'N/A'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Limite Diário</p>
              <p className="text-sm font-medium">{provider.settings?.daily_limit || provider.daily_limit || 30} atendimentos</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Cadastro</p>
              <p className="text-sm font-medium">{formatDate(provider.created_at)}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">E-mail verificado</p>
              <p className="text-sm font-medium">{provider.email_verified ? 'Sim' : 'Não'}</p>
            </div>
          </div>

          {/* Contato e Endereço */}
          <div className="mt-4 pt-4 border-t grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="provider-contact-info">
            <div className="flex items-start gap-2">
              <Mail className="w-4 h-4 text-slate-400 mt-1 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-xs text-slate-500">E-mail</p>
                <p className="text-sm font-medium truncate" data-testid="provider-email">{provider.email || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Phone className="w-4 h-4 text-slate-400 mt-1 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-xs text-slate-500">Telefone</p>
                <p className="text-sm font-medium" data-testid="provider-phone">{provider.phone || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <MapPin className="w-4 h-4 text-slate-400 mt-1 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-xs text-slate-500">Endereço</p>
                <p className="text-sm font-medium" data-testid="provider-address">
                  {fullAddress || 'N/A'}
                  {address.cep && <span className="text-slate-400"> • CEP {address.cep}</span>}
                </p>
              </div>
            </div>
          </div>

          {/* Score Consolidado */}
          {providerScore.score !== undefined && (
            <div className="mt-4 pt-4 border-t grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="provider-score-block">
              <div>
                <p className="text-xs text-slate-500">Score Geral</p>
                <p className="text-lg font-bold text-indigo-700" data-testid="provider-score-value">{providerScore.score}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Qualidade</p>
                <Badge className={
                  providerScore.quality_status === 'good' ? 'bg-green-100 text-green-700' :
                  providerScore.quality_status === 'warning' ? 'bg-yellow-100 text-yellow-700' :
                  providerScore.quality_status === 'critical' ? 'bg-red-100 text-red-700' :
                  'bg-gray-100 text-gray-700'
                }>{providerScore.quality_status || 'N/A'}</Badge>
              </div>
              <div>
                <p className="text-xs text-slate-500">Avaliação Média</p>
                <p className="text-sm font-medium">{providerScore.rating_average ?? 0} ★</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Taxa Cancel.</p>
                <p className="text-sm font-medium">{((providerScore.cancel_rate || 0) * 100).toFixed(1)}%</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Serviços Concluídos</p>
                <p className="text-sm font-medium">{providerScore.completed_services || 0}</p>
              </div>
            </div>
          )}
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="appointments" className="space-y-4">
          <TabsList className="grid grid-cols-4 w-full max-w-lg">
            <TabsTrigger value="appointments" data-testid="tab-appointments">Agendamentos</TabsTrigger>
            <TabsTrigger value="transfers" data-testid="tab-transfers">Repasses</TabsTrigger>
            <TabsTrigger value="reviews" data-testid="tab-reviews">Avaliações</TabsTrigger>
            <TabsTrigger value="fraud" data-testid="tab-fraud">Antifraude</TabsTrigger>
          </TabsList>

          {/* Agendamentos */}
          <TabsContent value="appointments">
            <Card className="p-6" data-testid="appointments-content">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5" /> Histórico de Agendamentos
              </h3>
              {appointments.length > 0 ? (
                <div className="space-y-3">
                  {appointments.slice(0, 20).map((appt) => (
                    <div key={appt.id || appt._id} className="p-4 border rounded-lg flex items-center justify-between">
                      <div>
                        <p className="font-medium">{appt.client_name || 'Cliente'}</p>
                        <p className="text-sm text-slate-500">
                          {appt.date} às {appt.time} • {appt.service_type || 'Serviço'}
                        </p>
                      </div>
                      <Badge className={STATUS_COLORS[appt.status] || 'bg-gray-100 text-gray-700'}>
                        {STATUS_LABELS[appt.status] || appt.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Calendar className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Nenhum agendamento</p>
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Repasses */}
          <TabsContent value="transfers">
            <Card className="p-6" data-testid="transfers-content">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <DollarSign className="w-5 h-5" /> Histórico de Repasses
                </h3>
                <div className="flex gap-3 text-sm">
                  <div className="text-right">
                    <p className="text-xs text-slate-500">Total recebido</p>
                    <p className="font-bold text-emerald-700" data-testid="transfers-total-completed">{formatCurrency(stats.total_earnings)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-slate-500">Pendente</p>
                    <p className="font-bold text-amber-700" data-testid="transfers-total-pending">{formatCurrency(stats.pending_earnings)}</p>
                  </div>
                </div>
              </div>
              {transfers.length > 0 ? (
                <div className="space-y-3">
                  {transfers.map((transfer) => (
                    <div key={transfer.id || transfer._id} className="p-4 border rounded-lg flex items-center justify-between" data-testid="transfer-row">
                      <div>
                        <p className="font-medium">{formatCurrency(transfer.amount)}</p>
                        <p className="text-sm text-slate-500">
                          {formatDate(transfer.created_at)}
                          {transfer.processed_at && ` • Processado em ${formatDate(transfer.processed_at)}`}
                        </p>
                      </div>
                      <Badge className={STATUS_COLORS[transfer.status] || 'bg-gray-100 text-gray-700'}>
                        {STATUS_LABELS[transfer.status] || transfer.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <DollarSign className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Nenhum repasse</p>
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Avaliações */}
          <TabsContent value="reviews">
            <Card className="p-6" data-testid="reviews-content">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Star className="w-5 h-5" /> Avaliações Recebidas
              </h3>
              {reviews.length > 0 ? (
                <div className="space-y-3">
                  {reviews.map((review) => (
                    <div key={review.id || review._id} className="p-4 border rounded-lg" data-testid="review-row">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex gap-0.5">
                          {[...Array(5)].map((_, i) => (
                            <Star 
                              key={`star-${review.id}-${i}`}
                              className={`w-4 h-4 ${i < (review.rating || 0) ? 'text-amber-500 fill-amber-500' : 'text-gray-200'}`}
                            />
                          ))}
                          <span className="ml-2 text-sm font-medium">{review.rating || 0}/5</span>
                        </div>
                        <span className="text-xs text-slate-400">{formatDate(review.created_at)}</span>
                      </div>
                      <p className="text-sm">{review.comment || 'Sem comentário'}</p>
                      <p className="text-xs text-slate-500 mt-1">Por: {review.client_name || 'Cliente'}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Star className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Nenhuma avaliação</p>
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Antifraude */}
          <TabsContent value="fraud">
            <Card className="p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" /> Logs de Antifraude
              </h3>
              {fraudLogs.length > 0 ? (
                <div className="space-y-3">
                  {fraudLogs.map((log) => (
                    <div key={log.id || log._id} className="p-4 border border-red-200 bg-red-50 rounded-lg">
                      <p className="font-medium text-red-800">{log.violation_type || 'Violação'}</p>
                      <p className="text-sm text-red-600">{log.description || 'Sem descrição'}</p>
                      <p className="text-xs text-red-500 mt-1">{formatDate(log.created_at)}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-green-600">
                  <CheckCircle className="w-12 h-12 mx-auto mb-2" />
                  <p>Nenhuma violação registrada</p>
                </div>
              )}
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
}
